"use client";

/**
 * React Query mutations para operações de escrita de autenticação
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  clearTokens,
  setTokens,
} from "./auth-storage";
import {
  confirmEmailService,
  recoverPasswordService,
  impersonateUserService,
  selectInstanceService,
  signInService,
  signOutService,
  signUpService,
  unimpersonateUserService,
} from "./auth.service";
import {
  ConfirmEmailType,
  ImpersonateUserType,
  RecoverPasswordType,
  SelectInstanceType,
  SignInType,
  SignUpType,
} from "./auth.type";

/**
 * Hook para mutation de login
 * Salva os tokens no localStorage e invalida queries relacionadas
 */
export function useSignInMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SignInType) => {
      const response = await signInService(data);

      if (response.status === "success" && response.data) {
        setTokens(response.data);
      }

      return response;
    },
    onSuccess: () => {
      // Invalidar queries relacionadas à autenticação
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
      queryClient.invalidateQueries({ queryKey: ["account", "me"] });
    },
    onError: (error) => {
      console.error("Erro ao realizar login:", error);
    },
  });
}

/**
 * Hook para mutation de registro
 * Salva os tokens no localStorage e invalida queries relacionadas
 */
export function useSignUpMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SignUpType) => {
      const response = await signUpService(data);

      if (response.status === "success" && response.data) {
        setTokens(response.data);
      }

      return response;
    },
    onSuccess: () => {
      // Invalidar queries relacionadas à autenticação
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
      queryClient.invalidateQueries({ queryKey: ["account", "me"] });
    },
    onError: (error) => {
      console.error("Erro ao realizar cadastro:", error);
    },
  });
}

/**
 * Hook para mutation de recuperação de senha
 */
export function useRecoverPasswordMutation() {
  return useMutation({
    mutationFn: async (data: RecoverPasswordType) => {
      return await recoverPasswordService(data);
    },
    onError: (error) => {
      console.error("Erro ao solicitar recuperação de senha:", error);
    },
  });
}

/**
 * Hook para mutation de logout
 * Remove os tokens do localStorage e invalida queries relacionadas
 */
export function useSignOutMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        await signOutService();
      } catch (error) {
        // Mesmo com erro, limpar tokens locais
        console.error("Erro ao realizar logout na API:", error);
      } finally {
        clearTokens();
      }
    },
    onSuccess: () => {
      // Limpar todas as queries em cache
      queryClient.clear();
    },
    onError: (error) => {
      console.error("Erro ao realizar logout:", error);
    },
  });
}

/**
 * Hook para mutation de seleção de instância
 * Atualiza os tokens no localStorage
 */
export function useSelectInstanceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SelectInstanceType) => {
      const response = await selectInstanceService(data);

      if (response.status === "success" && response.data) {
        setTokens(response.data);
      }

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
      queryClient.invalidateQueries({ queryKey: ["account", "me"] });
    },
    onError: (error) => {
      console.error("Erro ao selecionar instância:", error);
    },
  });
}

/**
 * Hook para mutation de personificação de usuário
 * Atualiza os tokens no localStorage com os dados do usuário personificado
 */
export function useImpersonateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ImpersonateUserType) => {
      const response = await impersonateUserService(data);

      if (response.status === "success" && response.data) {
        setTokens(response.data);
      }

      return response;
    },
    onSuccess: () => {
      // Invalidar todas as queries para recarregar com dados do usuário personificado
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["account"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["workers"] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: (error) => {
      console.error("Erro ao personificar usuário:", error);
    },
  });
}

/**
 * Hook para mutation de despersonificação
 * Restaura os tokens do usuário original no localStorage
 */
export function useUnimpersonateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await unimpersonateUserService();

      if (response.status === "success" && response.data) {
        setTokens(response.data);
      }

      return response;
    },
    onSuccess: () => {
      // Invalidar todas as queries para recarregar com dados do usuário original
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["account"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      queryClient.invalidateQueries({ queryKey: ["workers"] });
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: (error) => {
      console.error("Erro ao despersonificar usuário:", error);
    },
  });
}

/**
 * Hook para mutation de confirmação de email
 */
export function useConfirmEmailMutation() {
  return useMutation({
    mutationFn: async (data: ConfirmEmailType) => {
      return await confirmEmailService(data);
    },
    onError: (error) => {
      console.error("Erro ao confirmar email:", error);
    },
  });
}
